import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { type OnBoardingComponentProps } from "@src/app/onboarding/components/model";
// eslint-disable-next-line no-restricted-imports
import { OnboardingRouterPath } from "@src/app/routing/constant/onboardingRoute";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import { type AssessmentEvent } from "@src/appV2/SkillsAssessment/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isAfter, isValid } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";

import { ScheduleOrientation } from "../Schedule/ScheduleOrientation";

export function ScheduledOrientationOnboarding(props: OnBoardingComponentProps) {
  const { nextScreen, previousScreen, nextSection } = props;

  const worker = useDefinedWorker();
  const history = useHistory();
  const location = useLocation();

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SCHEDULED_ONBOARDING_PAGE_VIEWED);

  const queryFailureHandler = () => {
    if (isDefined(nextSection)) {
      history.push(nextSection);
    }
  };

  const { showSuccessToast } = useToast();

  const { skillsAssessmentConfig } = useSkillsAssessmentFeatureFlags();
  const {
    data: assessments,
    isLoading,
    isRefetching,
    isSuccess,
    refetch,
  } = useSkillsAssessments(
    {
      workerId: worker.userId,
      qualificationNames: skillsAssessmentConfig.availableQualificationNames,
      onFailure: queryFailureHandler,
    },
    {
      enabled: isDefined(worker.preference.qualification),
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const qualificationName =
    assessments?.included.find((included) => included.type === "qualification")?.attributes.name ??
    "";

  const latestScheduledEvent = assessments?.included?.find(
    (item) =>
      item.type === "assessment-event" &&
      item.id === assessments.data[0]?.relationships.latestScheduledEvent?.data?.id
  ) as AssessmentEvent | undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            top: true,
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
          background: "#fff",
        })}
      >
        <Stack p={5} height="100%">
          <IconButton
            iconType="chevron-left"
            size="medium"
            variant="outlined"
            aria-label="Back"
            onClick={() => {
              // location.key is a unique identifier for each location entry in the history stack
              // When you directly access a URL, location.key will be undefined, indicating there's
              // no previous page to go back to
              // When you navigate through the app normally, location.key will be defined, indicating
              // there is a previous page to go back to
              // It is more reliable than checking history.length
              if (isDefined(location.key)) {
                history.goBack();
              } else if (isDefined(previousScreen)) {
                history.push(previousScreen);
              }
            }}
          />

          <Stack alignItems="center" width="100%" pt={6} flex={1}>
            {isLoading && <CircularProgress />}

            {isSuccess && (
              <ScheduleOrientation
                isRefetchingData={isLoading || isRefetching}
                assessment={assessments.data[0]}
                qualificationName={qualificationName}
                initialScheduledFor={
                  isDefined(latestScheduledEvent?.attributes.scheduledFor) &&
                  isValid(new Date(latestScheduledEvent.attributes.scheduledFor)) &&
                  isAfter(new Date(latestScheduledEvent.attributes.scheduledFor), new Date())
                    ? latestScheduledEvent.attributes.scheduledFor
                    : undefined
                }
                onSkip={() => {
                  const nextSectionToNavigateTo =
                    nextSection ?? OnboardingRouterPath.ONBOARDING_WELCOME;

                  logEvent(
                    APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SKIP_ASSESSMENT_ALERT_CONFIRMED
                  );
                  history.push(nextSectionToNavigateTo);
                }}
                onSuccess={() => {
                  showSuccessToast("Demo Shift successfully scheduled!");
                  logEvent(
                    APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SCHEDULED_ONBOARDING_SCHEDULE_BUTTON_CLICKED
                  );
                  if (isDefined(nextScreen)) {
                    history.push(nextScreen);
                  }

                  void refetch();
                }}
                onError={() => {
                  if (isDefined(nextSection)) {
                    history.push(nextSection);
                  }
                }}
              />
            )}
          </Stack>
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}
